// Tournées de collecte (M13) : semaine affichée (une occurrence par tournée active et par jour, avec
// l'état de sa réalisation), liste des tournées, création d'une tournée par le responsable technique
// (secteur, jour, heure, opérateur, véhicule, points ordonnés ; code généré, D-049).
import { CODES_OPERATEURS, ETATS_REALISATION, JOURS_SEMAINE, OPERATEURS_TOURNEE, schemaCreationTournee } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, Dialogue, EtatVide, Lien, LienBouton, PuceEtat, Selection, Semaine, Tableau, type ColonneTableau } from '@cc/ui';
import { ChevronLeft, ChevronRight, Plus, Truck } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { ModuleNonActive, useModuleActif } from '../../commun/GardeModule';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerTournee, INVALIDER_PROPRETE, usePoints, usePrestataires, useCalendrier, useSecteurs, useTournees, useVehicules } from './api';
import { aujourdhuiIso, libelleDate, libelleJour, semaineDecalee, texteOccurrence, TON_REALISATION, typeOccurrence } from './logique';
import type { OperateurTournee, Tournee } from './types';
import styles from './proprete.module.css';
import commun from '../../commun/commun.module.css';

function FormulaireTournee({ onFermer }: { onFermer: () => void }) {
  const naviguer = useNavigate();
  const secteurs = useSecteurs({ actif: 'oui' });
  const vehicules = useVehicules();
  const prestataires = usePrestataires();
  const [v, setV] = useState({ nom: '', secteur_id: '', jour_semaine: '1', heure: '06:00', operateur: 'commune' as OperateurTournee, prestataire_id: '', vehicule_id: '', points: [] as string[] });
  const points = usePoints(v.secteur_id ? { secteur_id: v.secteur_id, actif: 'oui' } : {});
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationTournee.safeParse({
        nom: v.nom, secteur_id: v.secteur_id, jour_semaine: Number(v.jour_semaine), heure: v.heure, operateur: v.operateur,
        prestataire_id: v.operateur === 'prestataire' && v.prestataire_id ? v.prestataire_id : undefined, vehicule_id: v.vehicule_id || undefined, points: v.points,
      });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerTournee(corps.data);
    },
    succes: (t) => `Tournée ${t.nom} créée, code ${t.code}.`,
    invalider: INVALIDER_PROPRETE,
    apres: (t) => void naviguer(`/proprete/tournees/${t.id}`),
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  const basculerPoint = (id: string) => setV((x) => ({ ...x, points: x.points.includes(id) ? x.points.filter((p) => p !== id) : [...x.points, id] }));
  return (
    <Dialogue
      ouvert
      titre="Nouvelle tournée"
      description="Le code de la tournée est généré à partir des initiales du nom. Les points cochés sont parcourus dans l'ordre où vous les cochez."
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton>
          <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Créer la tournée</Bouton>
        </>
      }
    >
      <Champ libelle="Nom" value={v.nom} onChange={(e) => setV((x) => ({ ...x, nom: e.target.value }))} erreur={champ('nom')} required />
      <Selection libelle="Secteur" options={(secteurs.data ?? []).map((s) => ({ valeur: s.id, libelle: `${s.code} · ${s.nom}` }))} optionVide="Choisir un secteur" value={v.secteur_id} onChange={(e) => setV((x) => ({ ...x, secteur_id: e.target.value, points: [] }))} erreur={champ('secteur_id')} required />
      <div className={styles.grilleFormulaire}>
        <Selection libelle="Jour de la semaine" options={Object.entries(JOURS_SEMAINE).map(([n, l]) => ({ valeur: n, libelle: l }))} value={v.jour_semaine} onChange={(e) => setV((x) => ({ ...x, jour_semaine: e.target.value }))} />
        <Champ libelle="Heure de départ" type="time" value={v.heure} onChange={(e) => setV((x) => ({ ...x, heure: e.target.value }))} erreur={champ('heure')} required />
        <Selection libelle="Opérateur" options={CODES_OPERATEURS.map((o) => ({ valeur: o, libelle: OPERATEURS_TOURNEE[o] }))} value={v.operateur} onChange={(e) => setV((x) => ({ ...x, operateur: e.target.value as OperateurTournee }))} />
        {v.operateur === 'prestataire' ? (
          <Selection libelle="Prestataire" options={(prestataires.data ?? []).map((p) => ({ valeur: p.id, libelle: p.nom }))} optionVide="Choisir un prestataire" value={v.prestataire_id} onChange={(e) => setV((x) => ({ ...x, prestataire_id: e.target.value }))} erreur={champ('prestataire_id')} required />
        ) : null}
        <Selection libelle="Véhicule" options={(vehicules.data ?? []).filter((x) => x.actif).map((x) => ({ valeur: x.id, libelle: `${x.code} · ${x.immatriculation}` }))} optionVide="Aucun véhicule" value={v.vehicule_id} onChange={(e) => setV((x) => ({ ...x, vehicule_id: e.target.value }))} facultatif />
      </div>
      <fieldset className={commun.pile}>
        <legend className="cc-etiquette">Points de collecte du secteur ({v.points.length} retenu{v.points.length > 1 ? 's' : ''})</legend>
        {v.secteur_id && (points.data ?? []).length === 0 ? <p className={commun.na}>Aucun point de collecte actif dans ce secteur.</p> : null}
        <ul className={styles.pointsListe}>
          {(points.data ?? []).map((p) => (
            <li key={p.id}>
              <span className={styles.ordre}>{v.points.includes(p.id) ? v.points.indexOf(p.id) + 1 : ''}</span>
              <CaseACocher libelle={`${p.code} · ${p.nom} (${p.type_libelle})`} checked={v.points.includes(p.id)} onChange={() => basculerPoint(p.id)} />
            </li>
          ))}
        </ul>
      </fieldset>
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageTournees() {
  const { contexte } = useSession();
  const actif = useModuleActif('M13');
  const [semaine, setSemaine] = useState('');
  const calendrier = useCalendrier(semaine);
  const tournees = useTournees();
  const [creation, setCreation] = useState(false);
  const aujourdhui = calendrier.data?.aujourdhui ?? aujourdhuiIso();
  const lundi = calendrier.data?.lundi ?? semaineDecalee(semaine || aujourdhui, 0);
  const colonnes: ColonneTableau<Tournee>[] = [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (t) => <Lien href={`/proprete/tournees/${t.id}`} className={commun.reference}>{t.code}</Lien>, valeurTri: (t) => t.code },
    { id: 'nom', entete: 'Tournée', cellule: (t) => t.nom, valeurTri: (t) => t.nom },
    { id: 'secteur', entete: 'Secteur', cellule: (t) => t.secteur.nom ?? t.secteur.code ?? '', valeurTri: (t) => t.secteur.nom ?? '' },
    { id: 'jour', entete: 'Jour et heure', cellule: (t) => `${libelleJour(t.jour_semaine)} ${t.heure}`, valeurTri: (t) => t.jour_semaine * 10000 + Number(t.heure.replace(':', '')) },
    { id: 'operateur', entete: 'Opérateur', cellule: (t) => (t.operateur === 'prestataire' ? `${t.operateur_libelle}${t.prestataire?.nom ? ` : ${t.prestataire.nom}` : ''}` : t.equipe?.nom ?? t.operateur_libelle) },
    { id: 'points', entete: 'Points', numerique: true, cellule: (t) => t.points.length.toLocaleString('fr-FR'), valeurTri: (t) => t.points.length },
    { id: 'derniere', entete: 'Dernière réalisation', cellule: (t) => (t.derniere_realisation ? <span className={commun.ligne}>{date(t.derniere_realisation.date)} <PuceEtat ton={TON_REALISATION[t.derniere_realisation.etat]} libelle={ETATS_REALISATION[t.derniere_realisation.etat]} /></span> : 'aucune'), valeurTri: (t) => t.derniere_realisation?.date ?? '' },
    { id: 'etat', entete: 'État', cellule: (t) => (t.actif ? <PuceEtat ton="ok" libelle="Active" /> : <PuceEtat ton="neutre" libelle="Suspendue" />), valeurTri: (t) => (t.actif ? 1 : 0) },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Déchets et propreté"
        titre="Tournées de collecte"
        description="Tournées planifiées par secteur et jour de la semaine, réalisations saisies au bureau ou reçues des agents de terrain."
        actions={
          <>
            <LienBouton href="/proprete/secteurs" variante="secondaire">Secteurs et carte</LienBouton>
            <LienBouton href="/proprete/realisations" variante="secondaire">Réalisations</LienBouton>
            <LienBouton href="/proprete/vehicules" variante="secondaire">Véhicules</LienBouton>
            <LienBouton href="/proprete/couts" variante="secondaire">Coûts</LienBouton>
            {peut(contexte, 'dechets', 'creer') && actif !== false ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouvelle tournée</Bouton> : null}
          </>
        }
      />
      {actif === false ? <ModuleNonActive module="M13" retour={{ href: '/', libelle: 'Pilotage' }} /> : null}
      <div className={styles.semaineBarre}>
        <Bouton variante="secondaire" icone={ChevronLeft} onClick={() => setSemaine(semaineDecalee(lundi, -1))}>Semaine précédente</Bouton>
        <h2 className={styles.semaineTitre}>Semaine du {libelleDate(lundi)}</h2>
        <span className={commun.ligne}>
          <Bouton variante="secondaire" onClick={() => setSemaine('')} disabled={!semaine}>Cette semaine</Bouton>
          <Bouton variante="secondaire" icone={ChevronRight} onClick={() => setSemaine(semaineDecalee(lundi, 1))}>Semaine suivante</Bouton>
        </span>
      </div>
      <EtatRequete chargement={calendrier.isPending} erreur={calendrier.error} onReessayer={() => void calendrier.refetch()} lot="L3-B" fonction="Le calendrier des tournées">
        <Semaine
          libelle="Tournées de la semaine"
          aujourdhui={aujourdhui}
          vide="Aucune tournée"
          jours={(calendrier.data?.jours ?? []).map((j) => ({
            date: j.date,
            libelle: libelleDate(j.date),
            evenements: j.occurrences.map((o) => ({
              id: `${o.tournee.id}-${j.date}`,
              titre: <Lien href={`/proprete/tournees/${o.tournee.id}`}>{texteOccurrence(o.tournee, o.realisation, j.date, aujourdhui)}</Lien>,
              type: typeOccurrence(o.realisation, j.date, aujourdhui),
              libelleType: o.realisation ? ETATS_REALISATION[o.realisation.etat] : j.date < aujourdhui ? 'Non renseignée' : 'À réaliser',
            })),
          }))}
        />
      </EtatRequete>
      <EtatRequete chargement={tournees.isPending} erreur={tournees.error} onReessayer={() => void tournees.refetch()} lot="L3-B" fonction="La liste des tournées">
        {(tournees.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune tournée" icone={Truck}>
            <p>Aucune tournée n&apos;est planifiée. Créez d&apos;abord un secteur et ses points de collecte, puis une tournée.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Tournées" colonnes={colonnes} lignes={tournees.data ?? []} cleLigne={(t) => t.id} cleColonnes="proprete-tournees" />
        )}
      </EtatRequete>
      {creation ? <FormulaireTournee onFermer={() => setCreation(false)} /> : null}
    </>
  );
}
